import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '@artosera/ui';
import {
  useReglagesNegociation,
  useReglerNegociation,
  type ReglagesNegociation,
} from '../../api/presentation.js';
import { useArtistes } from '../../api/artistes.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ErreurPresentation } from '../presentation/ErreurPresentation.js';
import styles from '../presentation/Presentation.module.css';

export function NegociationPage() {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie, compte } = useShellContext();
  const peutLire =
    galerieAModule(galerie, 'rendez-vous-client') && galerieALaction(galerie, 'presentation:read');
  const reglages = useReglagesNegociation(galerieSlug, peutLire);
  if (!peutLire) return <p role="alert">{t('interdit')}</p>;
  return (
    <div className={styles.page}>
      <h1>{t('reglages.titre')}</h1>
      <p>{t('reglages.description')}</p>
      {reglages.isPending ? <p role="status">{t('chargement')}</p> : null}
      <ErreurPresentation erreur={reglages.error} recharger={() => void reglages.refetch()} />
      {reglages.data ? (
        <FormulaireNegociation
          key={`${compte.id}:${galerieSlug}:${reglages.data.reglages.version}`}
          reglages={reglages.data.reglages}
          recharger={() => void reglages.refetch()}
        />
      ) : null}
    </div>
  );
}
function FormulaireNegociation({
  reglages,
  recharger,
}: {
  reglages: ReglagesNegociation;
  recharger: () => void;
}) {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie } = useShellContext();
  const peutEcrire = galerieALaction(galerie, 'settings:write');
  const artistes = useArtistes(
    galerieSlug,
    { tri: 'nom', parPage: 200 },
    { enabled: galerieALaction(galerie, 'artists:read') },
  );
  const regler = useReglerNegociation(galerieSlug);
  const [defaut, setDefaut] = useState(String(reglages.remiseMaxPointsDeBase / 100));
  const [seuil, setSeuil] = useState(
    reglages.seuilApprobationPointsDeBase === null
      ? ''
      : String(reglages.seuilApprobationPointsDeBase / 100),
  );
  const [parArtiste, setParArtiste] = useState(
    reglages.parArtiste.map((artiste) => ({
      artisteId: artiste.artisteId,
      remise: String(artiste.remiseMaxPointsDeBase / 100),
    })),
  );
  const [artisteId, setArtisteId] = useState('');
  const [recherche, setRecherche] = useState('');
  const [invalide, setInvalide] = useState(false);
  const choixArtistes = useArtistes(
    galerieSlug,
    { q: recherche, tri: 'nom', parPage: 200 },
    { enabled: peutEcrire && galerieALaction(galerie, 'artists:read') },
  );
  const disponibles = (choixArtistes.data?.artistes ?? []).filter(
    (artiste) => !parArtiste.some((ligne) => ligne.artisteId === artiste.id),
  );
  const points = (texte: string) => Math.round(Number(texte) * 100);
  const estValide = (texte: string) => /^\d+(?:[.]\d{1,2})?$/.test(texte) && Number(texte) <= 100;
  return (
    <form
      className={styles.formulaire}
      onSubmit={(event) => {
        event.preventDefault();
        if (!peutEcrire || regler.isPending) return;
        if (
          !estValide(defaut) ||
          (seuil !== '' && !estValide(seuil)) ||
          parArtiste.some((ligne) => !estValide(ligne.remise))
        ) {
          setInvalide(true);
          return;
        }
        setInvalide(false);
        regler.mutate({
          remiseMaxPointsDeBase: points(defaut),
          seuilApprobationPointsDeBase: seuil === '' ? null : points(seuil),
          parArtiste: parArtiste.map((ligne) => ({
            artisteId: ligne.artisteId,
            remiseMaxPointsDeBase: points(ligne.remise),
          })),
          version: reglages.version,
        });
      }}
    >
      <label className={styles.champ}>
        {t('reglages.defaut')}
        <Input
          type="number"
          min={0}
          max={100}
          step="0.01"
          required
          disabled={!peutEcrire || regler.isPending}
          value={defaut}
          onChange={(event) => setDefaut(event.target.value)}
        />
      </label>
      <label className={styles.champ}>
        {t('reglages.seuil')}
        <Input
          type="number"
          min={0}
          max={100}
          step="0.01"
          disabled={!peutEcrire || regler.isPending}
          value={seuil}
          aria-describedby="aide-seuil"
          onChange={(event) => setSeuil(event.target.value)}
        />
      </label>
      <p id="aide-seuil" className={styles.aide}>
        {t('reglages.sansSeuil')}
      </p>
      <h2>{t('reglages.parArtiste')}</h2>
      {parArtiste.map((ligne, index) => {
        const nom =
          artistes.data?.artistes.find((artiste) => artiste.id === ligne.artisteId)?.nom ??
          ligne.artisteId;
        return (
          <div className={styles.actions} key={ligne.artisteId}>
            <label className={styles.champ}>
              {nom}
              <Input
                aria-label={t('reglages.remise', { artiste: nom }) + ' — ' + nom}
                type="number"
                min={0}
                max={100}
                step="0.01"
                required
                value={ligne.remise}
                disabled={!peutEcrire || regler.isPending}
                onChange={(event) =>
                  setParArtiste(
                    parArtiste.map((artiste, position) =>
                      position === index ? { ...artiste, remise: event.target.value } : artiste,
                    ),
                  )
                }
              />
            </label>
            {peutEcrire ? (
              <Button
                type="button"
                variant="ghost"
                disabled={regler.isPending}
                onClick={() =>
                  setParArtiste(parArtiste.filter((_, position) => position !== index))
                }
              >
                {t('reglages.retirer', { artiste: nom })}
              </Button>
            ) : null}
          </div>
        );
      })}
      {peutEcrire && galerieALaction(galerie, 'artists:read') ? (
        <div className={styles.actions}>
          <label className={styles.champ}>
            {t('rechercheArtiste')}
            <Input value={recherche} onChange={(event) => setRecherche(event.target.value)} />
          </label>
          <Select
            aria-label={t('reglages.choisir')}
            value={artisteId}
            onValueChange={setArtisteId}
            placeholder={t('reglages.choisir')}
            options={disponibles.map((artiste) => ({ value: artiste.id, label: artiste.nom }))}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={!disponibles.some((artiste) => artiste.id === artisteId) || regler.isPending}
            onClick={() => {
              setParArtiste([...parArtiste, { artisteId, remise: defaut }]);
              setArtisteId('');
            }}
          >
            {t('reglages.ajouter')}
          </Button>
        </div>
      ) : null}
      {invalide ? <p role="alert">{t('reglages.valeurInvalide')}</p> : null}
      <ErreurPresentation
        erreur={regler.error ?? artistes.error ?? choixArtistes.error}
        recharger={recharger}
      />
      {peutEcrire ? (
        <Button type="submit" loading={regler.isPending}>
          {t('enregistrer')}
        </Button>
      ) : null}
    </form>
  );
}
